// Shared helpers + tiny presentational components for the Admin panel.
// Extracted from AdminPage.js during the iter66 frontend refactor.
import { axiosInstance as _axios } from '../../config/axios'; // re-export for child files
import { Switch } from '../../components/ui/switch';
export const axiosInstance = _axios;

export const fmt = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
export const fmtDT = d => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// Extract error message from API response (handles Pydantic validation errors)
export const getApiErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || e?.message || JSON.stringify(e)).join(', ') || fallback;
  }
  if (typeof detail === 'object' && (detail.msg || detail.message)) {
    return detail.msg || detail.message;
  }
  return fallback;
};

export const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B3A67]/30';
export const labelCls = 'block text-xs font-bold text-gray-500 mb-1';

export function Field({ label, required, children }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      {children}
    </div>
  );
}

export const ROLE_META = {
  regular:    { label: 'Regular',    color: 'bg-gray-100 text-gray-600' },
  player:     { label: 'Player',     color: 'bg-green-100 text-green-700' },
  creator:    { label: 'Creator',    color: 'bg-purple-100 text-purple-700' },
  team_owner: { label: 'Team Owner', color: 'bg-blue-100 text-blue-700' },
  sponsor:    { label: 'Sponsor',    color: 'bg-yellow-100 text-yellow-700' },
  admin:      { label: 'Admin',      color: 'bg-orange-100 text-orange-700' },
  super_admin:{ label: 'Super Admin',color: 'bg-red-100 text-red-700' },
};

export function RoleBadge({ role }) {
  const m = ROLE_META[role] || { label: role, color: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${m.color}`}>{m.label}</span>;
}

// Shop listing switch for product / service forms. `noun` is "product" or "service".
export function ListingToggle({ checked, onChange, noun }) {
  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${
        checked ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div>
        <p className="text-sm font-extrabold text-[#2B3A67]">
          {checked ? 'Listed in shop' : 'Hidden from shop'}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {checked
            ? `Customers can see and buy this ${noun}.`
            : `Only admins can see this ${noun}. It can't be bought or booked.`}
        </p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        aria-label={`List this ${noun} in the shop`}
        data-testid="listing-toggle"
        className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
      />
    </div>
  );
}

// "Hidden" pill for admin product / service lists.
export function HiddenBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-600 uppercase tracking-wide">
      <i className="ph-bold ph-eye-slash" /> Hidden
    </span>
  );
}

export function StatusBadge({ status }) {
  const map = {
    pending:   'bg-yellow-100 text-yellow-700',
    approved:  'bg-green-100 text-green-700',
    rejected:  'bg-red-100 text-red-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${map[status] || 'bg-gray-100 text-gray-500'}`}>{status}</span>;
}

// Above the bottom nav (z 1000) so its footer buttons are never covered.
export function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[1100] bg-black/50 flex items-center justify-center px-4 py-4"
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className={`bg-white rounded-3xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[80vh] flex flex-col shadow-2xl`}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-extrabold text-[#2B3A67]">{title}</h2>
          <button onClick={onClose}><i className="ph-bold ph-x text-xl text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 pt-4">
          {children}
        </div>
      </div>
    </div>
  );
}

export function StatCard({ icon, label, value, color }) {
  return (
    <div className={`rounded-2xl p-4 ${color} flex items-center gap-4`}>
      <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
        <i className={`ph-bold ${icon} text-xl`} />
      </div>
      <div>
        <p className="text-xs font-bold opacity-70 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-extrabold">{value}</p>
      </div>
    </div>
  );
}
